import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { alpha, Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import type { UpToDateSkillsetCourse } from "../type";

export type UpToDateCourseHeroProps = Readonly<{
  course: UpToDateSkillsetCourse;
}>;

export default function UpToDateCourseHero({ course }: UpToDateCourseHeroProps) {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) minmax(280px, 5fr)" },
        gap: { xs: 3, md: 4 },
        pt: { xs: 3, md: 6 },
        pb: { xs: 4, md: 5 },
        alignItems: "stretch",
      }}
    >
      <Stack spacing={2.5} sx={{ justifyContent: "center" }}>
        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
          <Chip
            label="最新技能培訓"
            variant="outlined"
            sx={{
              borderRadius: 0,
              borderColor: alpha(theme.palette.primary.contrastText, 0.22),
              color: alpha(theme.palette.primary.contrastText, 0.88),
              bgcolor: alpha(theme.palette.primary.contrastText, 0.04),
              "& .MuiChip-label": {
                fontFamily: theme.typography.fontFamily,
              },
            }}
          />
          <Chip
            label={course.partner}
            variant="outlined"
            sx={{
              borderRadius: 0,
              borderColor: alpha(theme.palette.info.main, 0.28),
              color: theme.palette.primary.contrastText,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              "& .MuiChip-label": {
                fontFamily: theme.typography.fontFamily,
              },
            }}
          />
        </Stack>

        <Stack spacing={1.5}>
          <Typography variant="h2" color="primary.contrastText" sx={{ typography: { xs: "h3", md: "h2" } }}>
            {course.title}
          </Typography>
          <Typography variant="h5" sx={{ typography: { xs: "body1", md: "h5" }, color: alpha(theme.palette.primary.contrastText, 0.82) }}>
            {course.topic}
          </Typography>
          <Typography variant="body1" sx={{ color: alpha(theme.palette.primary.contrastText, 0.72), maxWidth: 720 }}>
            {course.content}
          </Typography>
        </Stack>
      </Stack>

      <CourseVisual courseTitle={course.title} media={course.media} />
    </Box>
  );
}

function CourseVisual({
  courseTitle,
  media,
}: Readonly<{
  courseTitle: string;
  media?: {
    kind: "image";
    src: string;
    alt: string;
  };
}>) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: 220, md: 360 },
        overflow: "hidden",
        border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}`,
        background: `
          radial-gradient(circle at 22% 24%, ${alpha(theme.palette.info.main, 0.34)} 0%, ${alpha(theme.palette.info.main, 0)} 38%),
          radial-gradient(circle at 80% 74%, ${alpha(theme.palette.primary.light, 0.18)} 0%, ${alpha(theme.palette.primary.light, 0)} 42%),
          linear-gradient(135deg, ${alpha(theme.palette.primary.contrastText, 0.06)} 0%, ${alpha(theme.palette.primary.dark, 0.58)} 100%)
        `,
      }}
    >
      {!media ? (
        <>
          <Box
            sx={{
              position: "absolute",
              inset: 28,
              border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}`,
              bgcolor: alpha(theme.palette.primary.dark, 0.24),
              backdropFilter: "blur(10px)",
            }}
          />
          <Stack
            sx={{
              position: "relative",
              zIndex: 1,
              minHeight: { xs: 220, md: 360 },
              p: 3,
              justifyContent: "space-between",
            }}
          >
            <AutoAwesomeOutlinedIcon sx={{ fontSize: 48, color: alpha(theme.palette.info.light, 0.92) }} />
            <Typography variant="h4" color="primary.contrastText">
              {courseTitle}
            </Typography>
          </Stack>
        </>
      ) : (
        <Box
          component="img"
          src={media.src}
          alt={media.alt}
          sx={{ width: 1, height: 1, minHeight: { xs: 220, md: 360 }, objectFit: "cover", display: "block" }}
        />
      )}
    </Box>
  );
}
